import { Text, useColorModeValue } from '@chakra-ui/react';
import { CellContext } from '@tanstack/react-table';
import invariant from 'tiny-invariant';
import {
  EditableCell,
  isEditableTableMeta,
  isEditingRow,
} from '../../table/Table';
import { useProjectHistory } from '../project-history/hooks/useProjectHistory';
import { DiffText } from '../project-history/DiffText';
import { TableEntry } from './OrderEntriesPanel';

export default function TitleCell<D extends TableEntry>(
  props: CellContext<D, string>
): React.ReactElement {
  const {
    getValue,
    table: {
      options: { meta },
    },
    row: {
      depth,
      original: { name, id, orderId },
    },
  } = props;
  invariant(isEditableTableMeta(meta));

  const dynamicValueColor = useColorModeValue('teal.500', 'teal.200');
  const { diff, isHistoryDrawerOpen } = useProjectHistory();

  // You will need to have the key for the column, orderId and entryId
  const entryKey = `project.orders.${orderId}.orderEntries.${id}`;
  const nameKey = `${entryKey}.name`;
  const nameDiff = diff?.[nameKey];

  let entryLabel: React.ReactNode = getValue();
  // the last condtion ensures that children do not get the diff text of the parent.
  if (
    isHistoryDrawerOpen &&
    nameDiff?.old &&
    nameDiff?.new &&
    nameDiff.new === name
  ) {
    entryLabel = <DiffText oldValue={nameDiff.old} newValue={nameDiff.new} />;
  }

  if (!isEditingRow(props.row, meta)) {
    return (
      <Text
        fontSize={'xs'}
        ml={2 * depth}
        color={
          depth > 0 && !isHistoryDrawerOpen ? dynamicValueColor : undefined
        }
        aria-label={`${name}-name`}
        noOfLines={1}
        wordBreak="break-all"
      >
        {entryLabel}
      </Text>
    );
  } else {
    return <EditableCell {...props} />;
  }
}
